import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { TransactionVersion } from "../../types/sale.type";
import { formatThaiFullDate } from "../../utils/date";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface VersionSelectorProps {
  versions: TransactionVersion[];
  selected?: TransactionVersion;
  onChange: (version: TransactionVersion) => void;
}

/**
 * Version picker for a bill's detail screen.
 *
 * Deliberately the same shape as the sale report's picker in
 * `components/Report/Details/Footer.tsx`: a "แก้ไขครั้งที่ N … เมื่อ" caption followed
 * by a `Select` keyed on `created_no`, options labelled with the full Thai datetime.
 * Reusing the wording means a user who has browsed report versions already knows what
 * this is.
 *
 * Two deliberate differences from the report's picker:
 *
 * - **It hides itself when there is only one version.** Bills are append-only, so the
 *   overwhelming majority have exactly one — a picker offering a single choice is
 *   noise on almost every bill. Sale reports are recalculated routinely, so theirs is
 *   nearly always meaningful.
 * - **The actor is only named when it is known.** The report's picker always renders
 *   "โดย {name} ({role})"; the bill tables only stamp an actor on a cancel, so most
 *   entries here have none. Rendering "โดย -" would read as a lookup failure rather
 *   than as an absent stamp, so the clause is dropped entirely instead.
 */
export default function VersionSelector({
  versions,
  selected,
  onChange,
}: VersionSelectorProps) {
  if (!selected || versions.length < 2) return null;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{ minWidth: 0, [compact]: { gap: 0.5 } }}
    >
      <Typography
        fontWeight={600}
        variant="caption"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap" }}
      >
        แก้ไขครั้งที่ {selected.created_no}
        {selected.created_by && (
          <>
            {" โดย "}
            <Typography variant="caption" component="span" fontWeight={800}>
              {selected.created_by}
            </Typography>
          </>
        )}
        {" เมื่อ"}
      </Typography>
      <Select
        size="small"
        sx={{ flexShrink: 0 }}
        value={selected.created_no}
        onChange={(e) => {
          const found = versions.find(
            (version) => version.created_no === e.target.value,
          );
          if (found) onChange(found);
        }}
      >
        {versions.map((version) => (
          <MenuItem key={version.created_no} value={version.created_no}>
            <Typography variant="caption" component="span" fontWeight={800}>
              {version.created_at
                ? formatThaiFullDate(version.created_at)
                : `เวอร์ชันที่ ${version.created_no}`}
            </Typography>
            {version.is_current && (
              <Typography
                variant="caption"
                component="span"
                sx={{ ml: 1 }}
                color="primary"
                fontWeight={800}
              >
                (ล่าสุด)
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
